/**
 * scrub.ts — the dynamic yellow line under a horizontal scroller (the founder's
 * 2026-10-27 request for /samples: "when scrolling horizontally through videos
 * on mobile the yellow line should increase and decrease dynamically to the left
 * and right"). Shared by the Samples page brand strips and the homepage rails.
 *
 * The line's yellow fill = the share of the strip seen so far: from the first
 * screenful (clientWidth / scrollWidth) to 1 at the end. It grows as the strip is
 * swiped forward and shrinks as it is swiped back, in both directions and in RTL
 * (an RTL scroller reports a negative scrollLeft, hence Math.abs). Writes one
 * custom property (default `--scrub`, 0→1) on `target`, rAF-batched; the fill's
 * CSS is `transform: scaleX(var(--scrub))` from the inline-start edge. When the
 * strip does not overflow, `target` gets `data-scrub-idle` (CSS hides it).
 */
export function bindScrub(
  scroller: HTMLElement,
  target: HTMLElement,
  prop = "--scrub",
): void {
  let pending = false;
  const measure = (): void => {
    pending = false;
    const total = scroller.scrollWidth;
    const view = scroller.clientWidth;
    if (total <= 0) return;
    const idle = total <= view + 1;
    target.toggleAttribute("data-scrub-idle", idle);
    const seen = Math.min(1, (Math.abs(scroller.scrollLeft) + view) / total);
    target.style.setProperty(prop, seen.toFixed(4));
  };
  const schedule = (): void => {
    if (pending) return;
    pending = true;
    window.requestAnimationFrame(measure);
  };
  scroller.addEventListener("scroll", schedule, { passive: true });
  if ("ResizeObserver" in window)
    new ResizeObserver(schedule).observe(scroller);
  else window.addEventListener("resize", schedule, { passive: true });
  measure();
}

/** Every `[data-scrub-for="<scroller id>"]` on the page. */
export function initScrubs(): void {
  for (const track of document.querySelectorAll<HTMLElement>(
    "[data-scrub-for]",
  )) {
    const scroller = document.getElementById(
      track.getAttribute("data-scrub-for") ?? "",
    );
    if (scroller) bindScrub(scroller, track);
  }
}
